'use client';

import { usePageAction } from '@ria-client/hooks/usePageAction';
import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import type { Vertical } from '@ria-client/supergraph/marketplace/constants';
import type { RoundupAdditionalRecommendations } from '@ria-client/supergraph/roundup/types';

export interface AdditionalRecommendationsProps {
  additionalRecommendations: RoundupAdditionalRecommendations;
  vertical: Vertical;
}

interface RecommendationLinkProps {
  href: string;
  displayName?: string;
  vertical: Vertical;
}

function RecommendationLink({
  href,
  displayName,
  vertical,
}: RecommendationLinkProps): React.JSX.Element {
  const linkCopy = displayName?.trim() || 'Review';
  const { ref, impressionId, ensureViewed } = useNWAElementImpression({
    component: 'nw_review_redirected',
    destinationUrl: href,
    linkCopy,
    sectionName: vertical,
  });
  const firePageAction = usePageAction({
    actionDestinationUrl: href,
    actionName: 'nw_review_redirected',
    actionSection: vertical,
    actionType: 'select',
    linkCopy,
    parentEventId: impressionId,
  });

  return (
    <a
      href={href}
      ref={ref}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => {
        ensureViewed();
        firePageAction();
      }}
    >
      {displayName}
    </a>
  );
}

export function AdditionalRecommendations({
  additionalRecommendations,
  vertical,
}: AdditionalRecommendationsProps): React.JSX.Element | null {
  if (!additionalRecommendations) return null;

  return (
    <section
      aria-labelledby="additional-recommendations-title"
      className="mx-auto mb-7 max-w-full md:mb-16 md:max-w-[82%]"
      data-testid="additional-recommendations"
    >
      <h2 id="additional-recommendations-title">
        Want to compare more options? Here are our other top picks:
      </h2>
      <ul className="list-inside list-disc py-0 pl-2">
        {additionalRecommendations.map((review, index) => {
          const { cta, displayName, id, reviewLink } = review.marketplaceEntity;
          const ctaLink = reviewLink || cta?.link;
          if (!ctaLink) return null;

          return (
            <li className="list-item px-0 py-1" key={`${id}-${index}`}>
              <RecommendationLink displayName={displayName} href={ctaLink} vertical={vertical} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
